import { Link, createRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { HelperError, type TranscriptResponse } from '../api/helper';
import { useDeleteTranscript, useSummarizeTranscript, useTranscript } from '../api/helper-queries';
import { useHelperStore } from '../stores/helper';
import { rootRoute } from './__root';

export const transcriptDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/transcripts/$id',
  component: TranscriptDetailPage,
});

interface TranscriptLine {
  text: string;
  at: number;
  source?: string;
  speaker?: string;
}

function TranscriptDetailPage() {
  const credentials = useHelperStore((s) => s.credentials);
  const { id } = transcriptDetailRoute.useParams();
  const transcript = useTranscript(id);
  const summarize = useSummarizeTranscript();
  const remove = useDeleteTranscript();

  const lines = useMemo<TranscriptLine[]>(() => {
    if (!transcript.data?.lines_json) return [];
    try {
      const parsed = JSON.parse(transcript.data.lines_json);
      return Array.isArray(parsed) ? (parsed as TranscriptLine[]) : [];
    } catch {
      return [];
    }
  }, [transcript.data?.lines_json]);

  if (!credentials) {
    return (
      <section>
        <h1 style={{ marginTop: 0 }}>Transcript</h1>
        <p>
          Pair the helper first. <Link to="/pair">Go to pairing →</Link>
        </p>
      </section>
    );
  }

  if (transcript.isLoading) {
    return <p>Loading transcript…</p>;
  }
  if (transcript.error) {
    return (
      <section>
        <p style={{ color: 'crimson' }}>{describeError(transcript.error)}</p>
        <Link to="/transcripts">← Back to transcripts</Link>
      </section>
    );
  }
  if (!transcript.data) return null;
  const t = transcript.data;

  return (
    <section style={{ maxWidth: 900 }}>
      <Link to="/transcripts">← Back to transcripts</Link>
      <h1 style={{ marginTop: 12, marginBottom: 4 }}>{hostnameOf(t.source_url) ?? 'Transcript'}</h1>
      <p style={{ color: '#666', marginTop: 0 }}>
        <a href={t.source_url} target="_blank" rel="noreferrer">
          {t.source_url}
        </a>
      </p>
      <p style={{ color: '#666' }}>
        {new Date(t.started_at_ms).toLocaleString()} ·{' '}
        {formatDuration(t.ended_at_ms - t.started_at_ms)} · {t.line_count} lines
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => downloadTranscriptText(t, lines)}
          style={{ padding: '6px 12px' }}
        >
          Download .txt
        </button>
        {t.summary_md && (
          <button type="button" onClick={() => downloadSummary(t)} style={{ padding: '6px 12px' }}>
            Download summary
          </button>
        )}
        <button
          type="button"
          onClick={() => summarize.mutate({ id: t.id, force: !!t.summary_md })}
          disabled={summarize.isPending}
          style={{
            padding: '6px 12px',
            background: '#0ea5e9',
            color: 'white',
            border: 'none',
            borderRadius: 4,
          }}
        >
          {summarize.isPending
            ? 'Summarizing…'
            : t.summary_md
              ? 'Regenerate summary'
              : 'Generate summary'}
        </button>
        <button
          type="button"
          onClick={() => {
            if (confirm('Delete this transcript? This cannot be undone.')) {
              remove.mutate(t.id, {
                onSuccess: () => window.history.back(),
              });
            }
          }}
          disabled={remove.isPending}
          style={{ padding: '6px 12px', color: 'crimson' }}
        >
          Delete
        </button>
      </div>

      {summarize.error && <p style={{ color: 'crimson' }}>{describeError(summarize.error)}</p>}

      {t.summary_md && (
        <section
          style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            padding: 16,
            marginBottom: 24,
          }}
        >
          <h2 style={{ marginTop: 0 }}>Summary</h2>
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              fontFamily: 'inherit',
              margin: 0,
              fontSize: 14,
            }}
          >
            {t.summary_md}
          </pre>
          {t.summary_model && (
            <p style={{ color: '#888', fontSize: 12, marginTop: 12, marginBottom: 0 }}>
              Generated by {t.summary_model}
              {t.summary_at_ms ? ` · ${new Date(t.summary_at_ms).toLocaleString()}` : ''}
            </p>
          )}
        </section>
      )}

      <h2>Transcript</h2>
      {lines.length === 0 ? (
        <p style={{ color: '#666' }}>No lines parsed.</p>
      ) : (
        <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {lines.map((line, i) => (
            <li
              key={`${line.at}-${i}`}
              style={{
                padding: '6px 0',
                borderBottom: '1px solid #f0f0f0',
                display: 'grid',
                gridTemplateColumns: '80px 120px 1fr',
                gap: 12,
                fontSize: 14,
              }}
            >
              <span style={{ color: '#999' }}>{formatTime(line.at - t.started_at_ms)}</span>
              <span style={{ color: '#555' }}>{line.speaker ?? line.source ?? ''}</span>
              <span>{line.text}</span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function downloadTranscriptText(t: TranscriptResponse, lines: TranscriptLine[]) {
  const header = `Transcript: ${t.source_url}\nStarted: ${new Date(t.started_at_ms).toISOString()}\n\n`;
  const body = lines
    .map((line) => {
      const speaker = line.speaker ? `${line.speaker}: ` : '';
      const ts = formatTime(line.at - t.started_at_ms);
      return `[${ts}] ${speaker}${line.text}`;
    })
    .join('\n');
  downloadBlob(`${header}${body}\n`, `transcript-${t.id}.txt`, 'text/plain');
}

function downloadSummary(t: TranscriptResponse) {
  if (!t.summary_md) return;
  downloadBlob(t.summary_md, `summary-${t.id}.md`, 'text/markdown');
}

function downloadBlob(content: string, filename: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function hostnameOf(url: string): string | null {
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
}

function formatTime(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '00:00';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  const totalSeconds = Math.round(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function describeError(e: unknown): string {
  if (e instanceof HelperError) {
    return `${e.problem?.title ?? 'helper error'} (${e.status})${e.problem?.detail ? `: ${e.problem.detail}` : ''}`;
  }
  if (e instanceof Error) return e.message;
  return String(e);
}
